import type React from "react";
import { createContext, useContext, useState } from "react";
import type { BoundTurnstileObject } from "react-turnstile";
import { isWeb } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { analyticsService } from "../../../lib/analytics/analytics";
import { useBoolVariation } from "../../../lib/flag";
const isBotControlOverrideEnabled = process.env.NEXT_PUBLIC_BOT_CONTROL_OVERRIDE_ENABLED === "true";
export type BotControlProvider = {
  getBotControlToken: () => string | undefined;
  resetBotControl: () => void;
  onBotControlVerified: (token: string, component: BoundTurnstileObject) => void;
  onBotControlExpired: () => void;
  isBotControlEnabled: boolean;
};
const initialContext: BotControlProvider = {
  getBotControlToken: () => undefined,
  resetBotControl: () => {
    throw new Error("Context not initialized");
  },
  onBotControlVerified: () => {
    throw new Error("Context not initialized");
  },
  onBotControlExpired: () => {
    throw new Error("Context not initialized");
  },
  isBotControlEnabled: false
};
export const BotControlContext = createContext<BotControlProvider>(initialContext);
type BotControlProviderProps = {
  children: React.ReactNode;
};
export const BotControlProvider = ({
  children
}: BotControlProviderProps) => {
  const [botControlToken, setBotControlToken] = useState<string | undefined>(undefined);
  const [botControlComponent, setBotControlComponent] = useState<BoundTurnstileObject | null>(null);

  // Bot control enabled by feature flag but can be forcefully overridden for local development.
  const isBotControlFeatureEnabled = useBoolVariation(Flag.BOT_CONTROL_ENABLED, false);
  const isBotControlEnabled = isWeb && (isBotControlFeatureEnabled || isBotControlOverrideEnabled);
  const contextValue: BotControlProvider = {
    getBotControlToken: () => botControlToken,
    resetBotControl: () => {
      setBotControlToken(undefined);
      botControlComponent?.reset();
    },
    onBotControlVerified: (token: string, component: BoundTurnstileObject) => {
      analyticsService.trackEvent("BotControlChallengeSolved", {});
      setBotControlToken(token);
      setBotControlComponent(component);
    },
    onBotControlExpired: () => {
      analyticsService.trackEvent("BotControlChallengeExpired", {});
      setBotControlToken(undefined);
      botControlComponent?.reset();
    },
    isBotControlEnabled
  };
  return <BotControlContext.Provider value={contextValue} data-sentry-element="BotControlContext.Provider" data-sentry-component="BotControlProvider" data-sentry-source-file="BotControlProvider.tsx">
      {children}
    </BotControlContext.Provider>;
};
export const useBotControl = (): BotControlProvider => {
  const context = useContext(BotControlContext);
  if (!context) {
    throw new Error("useBotControl must be used within a BotControlProvider");
  }
  return context;
};